import { Vector2 } from "../../common/vector2.js";
import { BackButtonHandler } from "../../navigation/back_button_handler.js";
import { BasicEventsController } from "../../touch_events_controller/basic_events_controller.js";
import { AbstractOverlay } from "./abstract_overlay.js";
// import { DevToDev } from "../../plugins/dev_to_dev_plugin.js";
import { OverlayId } from "./overlay_ids.js";
import { audioController } from "../../audio/audio_controller.js";
import { AudioClips } from "../../audio/audio_constants.js";
import { releaseHiddenCanvas, restoreReleasedCanvas } from "../../utils/canvas_memory.js";
import { CrashBreadcrumbs } from "../../crash/breadcrumbs.js";

export class OverlayManager
{
  public static ctx: CanvasRenderingContext2D;
  public static gl: WebGL2RenderingContext;

  private static overlay_stack: AbstractOverlay[] = [];
  private static canvas: HTMLCanvasElement;
  private static events_Controller: BasicEventsController;
  private static width: number;
  private static height: number;
  private static overlay_element: HTMLElement;
  private static interaction_blocked_until_ms = 0;

  private static is_down_outside_of_fverlay: boolean = false;

  public static init(
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number
  ): void
  {
    this.ctx = ctx;
    this.canvas = ctx.canvas;
    this.width = width;
    this.height = height;
    this.overlay_element = document.getElementById( 'overlayBackground' ) as HTMLElement;

    this.events_Controller = new BasicEventsController( this.canvas );
    this.events_Controller.init(
      ( p: Vector2 ) => this.handleDown( p ),
      ( p: Vector2 ) => this.handleUp( p ),
      ( p: Vector2 ) => this.handleMove( p )
    );
  }

  public static setScreenSize( width: number, height: number ): void
  {
    this.width = width;
    this.height = height;
  }

  public static update( now: number ): void
  {
    if ( this.overlay_stack.length === 0 )
      return;

    const top_overlay = this.topOverlay();
    top_overlay.update( now );
  }

  public static render(): void
  {
    if ( this.overlay_stack.length === 0 )
      return;

    const top_overlay = this.topOverlay();
    if ( !top_overlay.shouldBeRendered() )
      return;

    this.ctx.clearRect( 0, 0, this.width, this.height );
    top_overlay.render();
  }

  private static handleDown( p: Vector2 ): void
  {
    if ( this.overlay_stack.length === 0 )
      return;

    if ( this.isInsideOpenedOverlay( p ) )
    {
      const topOverlay = this.topOverlay();
      topOverlay.onTouchStart( p, performance.now() );
      return;
    }

    this.is_down_outside_of_fverlay = true;
  }

  private static handleUp( p: Vector2 ): void
  {
    if ( this.overlay_stack.length === 0 )
      return;

    const top_overlay = this.topOverlay();

    if ( this.isInsideOpenedOverlay( p ) )
    {
      top_overlay.onTouchEnd( p, performance.now() );

      if ( top_overlay.getCloseOnBackgroundClick() )
      {
        this.closeOverlay();
      }
      return;
    }

    if ( !this.is_down_outside_of_fverlay )
      return;

    this.is_down_outside_of_fverlay = false;
    if ( top_overlay.getCloseOnBackgroundClick() )
    {
      this.closeOverlay();
    }
  }

  private static handleMove( p: Vector2 ): void
  {
    if ( this.overlay_stack.length === 0 )
      return;

    if ( this.isInsideOpenedOverlay( p ) )
    {
      const top_overlay = this.topOverlay();
      top_overlay.onTouchMove( p, performance.now() );
      return;
    }
  }

  private static isInsideOpenedOverlay( p: Vector2 ): boolean
  {
    if ( this.overlay_stack.length === 0 )
      return false;

    const top_overlay = this.topOverlay();
    return top_overlay.containsPointGlobal( p );
  }

  public static openOverlay( overlay: AbstractOverlay ): void
  {
    void audioController.playSound( AudioClips.UI_CLICK );
    this.overlay_stack.push( overlay );
    CrashBreadcrumbs.add( 'dialog', `overlay open ${overlay.name?.() ?? 'unknown'}` );
    this.ctx.clearRect( 0, 0, this.width, this.height );
    this.show( overlay );
    BackButtonHandler.getInstance().updateBackButtonVisibility();

    // analytics: overlay_open + special case for revive offer
    // const name = overlay.name();
    // void DevToDev.sendEvent(`overlay_open_${name}`);
    // if (name === 'OfferAdOverlay') {
    //   void DevToDev.sendEvent('revive_offer_open');
    // }
  }

  public static closeOverlay(): void
  {
    if ( !this.isAnyOverlayOpened() )
      return;

    void audioController.playSound( AudioClips.UI_CLICK );
    // Consume the pointer release that closed the overlay so the same event
    // cannot click UI on the screen underneath it.
    this.interaction_blocked_until_ms = performance.now() + 100;

    const closed = this.overlay_stack.pop();
    CrashBreadcrumbs.add( 'dialog', `overlay close ${closed?.name?.() ?? 'unknown'}` );
    this.callOnClose( closed );

    if ( !this.isAnyOverlayOpened() )
    {
      this.ctx.clearRect( 0, 0, this.width, this.height );
      this.hide();
    }
    else
    {
      this.ctx.clearRect( 0, 0, this.width, this.height );
    }
    BackButtonHandler.getInstance().updateBackButtonVisibility();

    // analytics: overlay_close + special case for revive offer
    // const name = closed?.name?.() ?? 'unknown';
    // void DevToDev.sendEvent(`overlay_close_${name}`);
    // if (name === 'OfferAdOverlay') {
    //   void DevToDev.sendEvent('revive_offer_close');
    // }
  }

  private static canvas_anim: Animation | null = null;
  private static readonly EASE_OUT_QUAD = "cubic-bezier(0.25, 0.46, 0.45, 0.94)";

  public static useCssAnimations(): boolean
  {
    return typeof this.canvas?.animate === "function"
      && localStorage.getItem( "perf_diag_dialog_canvas_anim" ) !== "1";
  }

  public static cancelCanvasAnim(): void
  {
    if ( !this.canvas_anim )
      return;

    this.canvas_anim.cancel();
    this.canvas_anim = null;
  }

  public static animateAppearCss(
    overlay: AbstractOverlay,
    from_scale: number,
    duration_ms: number
  ): void
  {
    this.cancelCanvasAnim();

    const center = overlay.getCenterGlobal();
    this.canvas.style.transformOrigin = `${center.x}px ${center.y}px`;

    const anim = this.canvas.animate( [
      { transform: `scale(${from_scale})`, opacity: 0, easing: this.EASE_OUT_QUAD },
      { transform: "scale(1)", opacity: 1 },
    ], { duration: duration_ms, fill: "both" } );
    this.canvas_anim = anim;

    anim.onfinish = () =>
    {
      if ( this.canvas_anim === anim )
      {
        anim.cancel();
        this.canvas_anim = null;
      }
    };
  }

  private static background_anim: Animation | null = null;

  public static fadeInBackground( delay_ms: number, duration_ms: number ): void
  {
    this.cancelBackgroundAnim();

    if ( !this.overlay_element || typeof this.overlay_element.animate !== "function" )
      return;

    this.background_anim = this.overlay_element.animate( [
      { opacity: 0, easing: this.EASE_OUT_QUAD },
      { opacity: 1 },
    ], { delay: Math.max( 0, delay_ms ), duration: duration_ms, fill: "backwards" } );
  }

  private static cancelBackgroundAnim(): void
  {
    this.background_anim?.cancel();
    this.background_anim = null;
  }

  private static callOnClose( closed: AbstractOverlay | undefined ): void
  {
    try { closed?.onOverlayWillClose(); }
    catch ( e ) { console.warn( "OverlayManager: onOverlayWillClose failed", e ); }

    try { closed?.cleanup(); }
    catch ( e ) { console.warn( "OverlayManager: cleanup failed", e ); }
  }

  public static isAnyOverlayOpened(): boolean { return this.overlay_stack.length > 0; }

  public static isInteractionBlocked(): boolean
  {
    return this.isAnyOverlayOpened() || performance.now() < this.interaction_blocked_until_ms;
  }

  public static isOverlayOpen( overlay_id: OverlayId ): boolean
  {
    if ( !this.isAnyOverlayOpened() ) 
      return false;

    const top_overlay = this.topOverlaySafe();
    return top_overlay?.getId() === overlay_id;
  }

  private static topOverlaySafe(): AbstractOverlay | null
  {
    if ( this.overlay_stack.length === 0 )
      return null;

    return this.overlay_stack[this.overlay_stack.length - 1];
  }

  private static topOverlay(): AbstractOverlay
  {
    if ( this.overlay_stack.length === 0 )
      throw new Error( 'topOverlay(): No overlays are open' );

    return this.overlay_stack[this.overlay_stack.length - 1];
  }

  private static hide(): void
  {
    this.cancelCanvasAnim();
    this.cancelBackgroundAnim();
    this.canvas.style.display = "none";
    releaseHiddenCanvas( this.canvas );
    if ( this.overlay_element )
    {
      this.overlay_element.style.display = "none";
    }
  }

  private static show( overlay: AbstractOverlay ): void
  {
    restoreReleasedCanvas( this.canvas );
    this.canvas.style.display = "block";
    if ( this.overlay_element )
    {
      this.overlay_element.style.display = overlay.drawsOwnBackground() ? "none" : "block";
    }
  }
}
